import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Flashlight,
  Globe,
  Minus,
  PhoneCall,
  Pin,
  PinOff,
  Play,
  PlayCircle,
  Plug,
  Plus,
  Radar,
  RefreshCw,
  Route,
  Search,
  Settings2,
  ShieldCheck,
  Square,
  Terminal,
  Users,
  Video,
} from 'lucide-react';
import type {
  AccessEndpoint,
  AmiProvisionResult,
  DiscoveredDevice,
  DiscoverEvent,
  HealthState,
  MikrotikUsersResult,
  OpenPort,
  ProvisionProtocol,
  ProvisionResult,
  Resource,
  TorchFlow,
} from '@itr/shared';
import {
  ACCESS_SERVICES,
  ACCESS_SERVICE_SPECS,
  CAMERA_VENDOR_LABELS,
  PORT_PRESETS,
  accessHostFor,
  accessPortPreset,
  formatBitsPerSec,
  formatRelative,
  isAsterisk,
  isMikrotik,
  registeredHosts,
} from '@itr/shared';
import { api, apiFetchBlobUrl, apiUrl, streamNdjson } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, useFetch, useToast } from './ui';
import { DeviceFilterBar, useDeviceFilter, type DeviceFields } from './DeviceFilter';
import {
  DiagnosticProgress,
  DiagnosticResults,
  STREAMING_TOOLS,
  ToolOptions,
  useDiagnosticRun,
  usePingTransitionAlerts,
  type DiagnosticTool,
} from './diagnostics';

/**
 * Diagnostics and hands-on access, aimed at one resource.
 *
 * The same tools as the Diagnostics page, minus the target box: the server
 * takes the address from the resource, so what an operator sees on this page is
 * always about the device they are looking at.
 */

interface ResourceAccessData {
  resourceId: string;
  name: string;
  kind: string;
  health: HealthState;
  target: string | null;
  resolvedIp: string | null;
  unavailable: string | null;
  mikrotik: boolean;
  portPreset: string;
  adminUrl: string | null;
  endpoints: AccessEndpoint[];
  discoveredMacs: Array<{ label: string; value: string }>;
}

/* ================================================================== */
/* Services & open ports                                              */
/* ================================================================== */

interface ResourceServicesData {
  resourceId: string;
  name: string;
  target: string | null;
  preset: string;
  scannedAt: number | null;
  scanned: number;
  ports: OpenPort[];
  watched?: Array<{ port: number; host?: string }>;
  unavailable: string | null;
}

/**
 * The report: everything this device answers on, not just the handful of
 * doors Access checks. A real port scan against the preset that suits the
 * device's kind, run once when the page loads.
 */
export function ResourceServicesCard({ resource }: { resource: Resource }) {
  const { data, loading, error, reload } = useFetch(
    () => api.get<ResourceServicesData>(`/api/tools/resource/${resource.id}/services`),
    [resource.id],
  );

  const presetLabel = data ? (PORT_PRESETS[data.preset]?.label ?? data.preset) : null;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Radar size={14} />
        <span className="card-title">Services &amp; open ports</span>
        {data && !data.unavailable && (
          <Badge intent={data.ports.length > 0 ? 'good' : 'neutral'}>{data.ports.length} open</Badge>
        )}
        <span className="grow" />
        {loading && <span className="spinner" />}
        <button className="btn btn-sm" onClick={reload} disabled={loading}>
          <RefreshCw size={12} className={loading ? 'spin' : undefined} /> Rescan
        </button>
      </div>

      {error ? (
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>
          {error}
        </div>
      ) : data?.unavailable ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          {data.unavailable}
        </div>
      ) : data && data.ports.length === 0 ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          Nothing listening — scanned {data.scanned} port{data.scanned === 1 ? '' : 's'} ({presetLabel}) and none answered.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {data?.ports.some((p) => p.host) && <th style={{ width: 150 }}>Address</th>}
                <th style={{ width: 90 }}>Port</th>
                <th>Service</th>
                <th style={{ width: 130 }}>Status</th>
                <th>Banner</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.ports ?? []).map((port) => (
                <PortRow
                  key={`${port.host ?? ''}-${port.port}`}
                  resourceId={resource.id}
                  port={port}
                  showAddress={Boolean(data?.ports.some((p) => p.host))}
                  watched={Boolean(data?.watched?.some((w) => w.port === port.port && (w.host ?? '') === (port.host ?? '')))}
                  onWatchChanged={reload}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && !data.unavailable && (
        <div className="card-body subtle" style={{ fontSize: 11, paddingTop: 0 }}>
          Scanned {data.scanned} port{data.scanned === 1 ? '' : 's'} ({presetLabel} preset)
          {data.scannedAt ? ` · ${formatRelative(data.scannedAt)}` : ''}
        </div>
      )}
    </div>
  );
}

/**
 * One scanned, answering port. Every row here already connected from the
 * server's own side — that's what put it in the list — so its status is
 * always the green "open" a reachable service earns, never a warning.
 * "Via port" opens the same raw TCP relay the Access card's endpoints use,
 * just against a port that isn't one of the named access services.
 */
function PortRow({
  resourceId,
  port,
  showAddress,
  watched,
  onWatchChanged,
}: {
  resourceId: string;
  port: OpenPort;
  showAddress: boolean;
  watched: boolean;
  onWatchChanged: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const [watchBusy, setWatchBusy] = useState(false);

  const toggleWatch = async () => {
    setWatchBusy(true);
    try {
      await api.post(`/api/tools/resource/${resourceId}/watch-port`, {
        port: port.port,
        proto: port.proto ?? 'tcp',
        ...(port.host ? { host: port.host } : {}),
        service: port.service,
        watched: !watched,
      });
      toast(watched ? `Stopped watching port ${port.port}` : `Watching port ${port.port}`, 'good');
      onWatchChanged();
    } catch (err) {
      toast('Could not update the watch', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setWatchBusy(false);
    }
  };
  const [opening, setOpening] = useState(false);
  const [tunnel, setTunnel] = useState<{ id: string; address: string; expiresAt: number; viaMikrotik: boolean; viaMikrotikName: string | null } | null>(null);

  const openViaPort = async () => {
    setOpening(true);
    try {
      const opened = await api.post<{ id: string; host: string; port: number; expiresAt: number; viaMikrotik?: boolean; viaMikrotikName?: string | null }>(
        `/api/tools/resource/${resourceId}/tunnel`,
        { service: 'raw', port: port.port, ...(port.host ? { host: port.host } : {}) },
      );
      setTunnel({ id: opened.id, address: `${opened.host}:${opened.port}`, expiresAt: opened.expiresAt, viaMikrotik: Boolean(opened.viaMikrotik), viaMikrotikName: opened.viaMikrotikName ?? null });
    } catch (err) {
      toast('Could not open a tunnel', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setOpening(false);
    }
  };

  const copyAddress = async () => {
    if (!tunnel) return;
    if (await copyText(tunnel.address)) toast(`Copied ${tunnel.address}`, 'good');
    else toast('Could not copy', 'bad', 'Your browser blocked clipboard access — select the address instead.');
  };

  const closeTunnel = async () => {
    if (!tunnel) return;
    const { id } = tunnel;
    setTunnel(null);
    try {
      await api.delete(`/api/tools/resource/${resourceId}/tunnel/${id}`);
      toast('Tunnel closed', 'good');
    } catch {
      /* already reaped by the idle timer */
    }
  };

  return (
    <tr>
      {showAddress && (
        <td className="subtle" style={{ fontSize: 12 }}>
          {port.addressLabel ?? port.host ?? '—'}
        </td>
      )}
      <td className="mono">
        {port.port}
        {port.proto === 'udp' && <span className="subtle">/udp</span>}
      </td>
      <td>{port.service ? <Badge>{port.service}</Badge> : <span className="subtle">unknown</span>}</td>
      <td>
        <span className="row" style={{ gap: 6 }}>
          <Badge intent="good">open</Badge>
          {port.connectMs !== null && (
            <span className="subtle nums" style={{ fontSize: 11.5 }}>
              {port.connectMs} ms
            </span>
          )}
        </span>
      </td>
      <td className="mono subtle truncate" style={{ maxWidth: 320 }}>
        {port.banner ?? '—'}
      </td>
      <td>
       <div className="row" style={{ gap: 6, justifyContent: 'flex-end', alignItems: 'flex-start' }}>
        {can('resource:write') && (
          <button
            className={`btn btn-sm${watched ? ' btn-primary' : ''}`}
            onClick={toggleWatch}
            disabled={watchBusy}
            aria-pressed={watched}
            title={
              watched
                ? 'Watching — the resource turns degraded if this port stops answering. Click to stop.'
                : 'Watch this port — the resource turns degraded if it stops answering.'
            }
          >
            {watchBusy ? <span className="spinner" style={{ width: 12, height: 12 }} /> : watched ? <EyeOff size={12} /> : <Eye size={12} />}
            {watched ? 'Watching' : 'Watch'}
          </button>
        )}
        {/* The relay is TCP-only, so a UDP-only SIP port has nothing to tunnel. */}
        {(can('tools:proxy') || can('access:ticket')) && port.proto !== 'udp' && (
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            {tunnel ? (
              <span className="subtle" style={{ fontSize: 11, textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={copyAddress}
                  title={`Copy ${tunnel.address}`}
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                >
                  <span className="mono" style={{ textDecoration: 'underline dotted' }}>
                    {tunnel.address}
                  </span>{' '}
                  <Copy size={10} style={{ verticalAlign: 'middle' }} />
                </button>{' '}
                ·{' '}
                <button
                  type="button"
                  onClick={closeTunnel}
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  close now
                </button>
                {tunnel.viaMikrotik && (
                  <div className="subtle" style={{ fontSize: 10.5 }}>
                    via {tunnel.viaMikrotikName ? `MikroTik "${tunnel.viaMikrotikName}"` : 'a nearby MikroTik'} — its own address isn't directly reachable from here
                  </div>
                )}
              </span>
            ) : (
              <button
                className="btn btn-sm"
                onClick={openViaPort}
                disabled={opening}
                title="Forward a temporary port on the server straight to this port, for your own client to connect to. Closes after 10 minutes idle or an hour."
              >
                {opening ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null}
                Via port
              </button>
            )}
          </div>
        )}
       </div>
      </td>
    </tr>
  );
}

/* ================================================================== */
/* System services (Linux servers & FreePBX/Asterisk)                 */
/* ================================================================== */

interface SystemServiceInfo {
  name: string;
  description: string;
  subState: string;
}

interface SystemOpenPort {
  proto: 'tcp' | 'udp';
  address: string;
  port: number;
  process: string | null;
  pid: number | null;
}

interface ResourceSystemServicesData {
  allServices: SystemServiceInfo[];
  openPorts: SystemOpenPort[];
  pinnedServices: string[];
  /** Shown and still checked, but kept out of this resource's health. */
  ignoredServices?: string[];
  /** Which of the resource's registered addresses this data actually came
   *  from — a secondary address when the primary one didn't answer. */
  via: string | null;
}

/**
 * Everything systemd currently reports as active on the box, and everything
 * `ss` reports as listening — pulled straight over SSH, not a curated
 * watch-list. Pinning a service here is what makes it count toward this
 * resource's health from then on (see the servers/voip plugins' collectors);
 * ignoring it keeps it on the list but takes it back out of that health.
 */
export function ResourceSystemServicesCard({ resource, pluginId }: { resource: Resource; pluginId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const [pinning, setPinning] = useState<string | null>(null);
  // Collapsed by default: the list is long and rarely the first thing wanted.
  // Remembered per browser; storage can throw (private windows), so it is optional.
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem('itr.systemServices.open') === '1';
    } catch {
      return false;
    }
  });
  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem('itr.systemServices.open', next ? '1' : '0');
    } catch {
      /* not persisted */
    }
  };
  const { data, loading, error, reload } = useFetch(
    () => api.get<ResourceSystemServicesData>(`/api/plugins/${pluginId}/services/${resource.id}`),
    [resource.id],
  );

  if (!can('resource:read')) return null;

  const pinnedSet = new Set(data?.pinnedServices ?? []);
  const ignoredSet = new Set(data?.ignoredServices ?? []);
  // The list is systemd's *active* units, so a pinned service that has stopped
  // would silently vanish from it — exactly the one worth seeing.
  const activeNames = new Set((data?.allServices ?? []).map((s) => s.name));
  const stopped: Array<SystemServiceInfo & { stopped?: boolean }> = [...pinnedSet]
    .filter((name) => !activeNames.has(name))
    .map((name) => ({ name, description: '', subState: 'not running', stopped: true }));
  const services: Array<SystemServiceInfo & { stopped?: boolean }> = [...(data?.allServices ?? []), ...stopped].sort((a, b) => {
    const rank = Number(pinnedSet.has(b.name)) - Number(pinnedSet.has(a.name));
    return rank !== 0 ? rank : a.name.localeCompare(b.name);
  });
  const ports = data?.openPorts ?? [];

  async function toggleIgnore(name: string, ignored: boolean) {
    setPinning(name);
    try {
      await api.post(`/api/plugins/${pluginId}/ignore/${resource.id}`, { service: name, ignored });
      await reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update service', 'bad');
    } finally {
      setPinning(null);
    }
  }

  async function togglePin(name: string, pinned: boolean) {
    setPinning(name);
    try {
      await api.post(`/api/plugins/${pluginId}/pin/${resource.id}`, { service: name, pinned });
      await reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update pin', 'bad');
    } finally {
      setPinning(null);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <button
          className="btn btn-ghost btn-icon btn-sm"
          onClick={toggleOpen}
          aria-expanded={open}
          title={open ? 'Collapse' : 'Expand'}
          aria-label={open ? 'Collapse system services' : 'Expand system services'}
        >
          {open ? <Minus size={14} /> : <Plus size={14} />}
        </button>
        <Activity size={14} />
        <span className="card-title">System services &amp; ports</span>
        {data && <Badge intent={pinnedSet.size > 0 ? 'info' : 'neutral'}>{pinnedSet.size} pinned</Badge>}
        {data?.via && data.via !== String(resource.config.host ?? '') && (
          <span className="subtle" style={{ fontSize: 11.5 }} title="The configured host didn't answer — this data came from a secondary registered address instead.">
            via {data.via}
          </span>
        )}
        <span className="grow" />
        {loading && <span className="spinner" />}
        {open && (
          <button className="btn btn-sm" onClick={reload} disabled={loading}>
            <RefreshCw size={12} className={loading ? 'spin' : undefined} /> Refresh
          </button>
        )}
      </div>

      {!open ? null : error ? (
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>
          {error}
        </div>
      ) : data && services.length === 0 && ports.length === 0 ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          No data yet — set SSH credentials on this resource and wait for the next poll.
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 64 }} />
                  <th>Service</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => {
                  const pinned = pinnedSet.has(svc.name);
                  const ignored = ignoredSet.has(svc.name);
                  return (
                    <tr key={svc.name} style={ignored ? { opacity: 0.6 } : undefined}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {can('resource:write') && (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            title={pinned ? 'Unpin — stop watching this service' : 'Pin for monitoring'}
                            aria-label={pinned ? `Unpin ${svc.name}` : `Pin ${svc.name}`}
                            disabled={pinning === svc.name}
                            onClick={() => togglePin(svc.name, !pinned)}
                          >
                            {pinned ? <PinOff size={13} /> : <Pin size={13} />}
                          </button>
                        )}
                        {can('resource:write') && (pinned || ignored) && (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            title={
                              ignored
                                ? 'Count this service toward the resource status again'
                                : 'Ignore — keep watching, but a stopped service no longer affects the resource status'
                            }
                            aria-label={ignored ? `Stop ignoring ${svc.name}` : `Ignore ${svc.name}`}
                            disabled={pinning === svc.name}
                            onClick={() => toggleIgnore(svc.name, !ignored)}
                          >
                            {ignored ? <Eye size={13} /> : <EyeOff size={13} />}
                          </button>
                        )}
                      </td>
                      <td className="mono">
                        <span className="row" style={{ gap: 6 }}>
                          {svc.name}
                          {svc.stopped && <Badge intent={ignored ? 'neutral' : 'bad'}>not running</Badge>}
                          {ignored && <Badge>ignored</Badge>}
                        </span>
                      </td>
                      <td className="subtle truncate" style={{ maxWidth: 420 }}>
                        {svc.description || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card-body subtle" style={{ fontSize: 11, fontWeight: 600, paddingBottom: 4 }}>
            Open ports
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>Proto</th>
                  <th style={{ width: 150 }}>Address</th>
                  <th style={{ width: 80 }}>Port</th>
                  <th>Process</th>
                </tr>
              </thead>
              <tbody>
                {ports.map((p, i) => (
                  <tr key={`${p.proto}-${p.address}-${p.port}-${i}`}>
                    <td>
                      <Badge>{p.proto.toUpperCase()}</Badge>
                    </td>
                    <td className="mono subtle">{p.address}</td>
                    <td className="mono">{p.port}</td>
                    <td className="subtle">{p.process ? `${p.process}${p.pid ? ` (${p.pid})` : ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ================================================================== */
/* Access                                                             */
/* ================================================================== */

export function ResourceAccessCard({ resource, onConfigured }: { resource: Resource; onConfigured: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [amiSetup, setAmiSetup] = useState(false);
  // Probed once per visit, then on demand. Keying this on the resource's
  // `updatedAt` would re-probe after every collection run — five TCP connects a
  // minute for a page somebody left open on a wall display.
  const { data, loading, error, reload } = useFetch(
    () => api.get<ResourceAccessData>(`/api/tools/resource/${resource.id}`),
    [resource.id],
  );

  useEffect(() => {
    if (!data?.discoveredMacs.length) return;
    for (const mac of data.discoveredMacs) toast(`Found MAC ${mac.value} via ARP`, 'good', 'Saved to this resource.');
    onConfigured();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.discoveredMacs]);

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Plug size={14} />
        <span className="card-title">Access</span>
        {data?.mikrotik && <Badge intent="info">MikroTik</Badge>}
        <span className="grow" />
        {loading && <span className="spinner" />}
        <button className="btn btn-sm" onClick={reload} disabled={loading}>
          <RefreshCw size={12} className={loading ? 'spin' : undefined} /> Recheck
        </button>
        {can('resource:write') && (
          <button className="btn btn-sm" onClick={() => setEditing(true)}>
            <Settings2 size={12} /> Ports
          </button>
        )}
        {can('tools:provision') && data?.mikrotik && (
          <button className="btn btn-sm" onClick={() => setProvisioning(true)}>
            <ShieldCheck size={12} /> Set up protocol
          </button>
        )}
        {can('tools:provision') && isAsterisk(resource) && (
          <button className="btn btn-sm" onClick={() => setAmiSetup(true)}>
            <PhoneCall size={12} /> AMI manager
          </button>
        )}
      </div>

      {error ? (
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>
          {error}
        </div>
      ) : data?.unavailable ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          {data.unavailable}
        </div>
      ) : data && data.endpoints.length === 0 ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          No access services are configured for this resource.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Service</th>
                <th style={{ width: 190 }}>Address</th>
                <th style={{ width: 130 }}>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.endpoints ?? []).map((endpoint) => (
                <AccessRow key={`${endpoint.service}-${endpoint.host}`} resourceId={resource.id} endpoint={endpoint} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.resolvedIp && data.resolvedIp !== data.target && (
        <div className="card-body subtle" style={{ fontSize: 11.5, paddingTop: 0 }}>
          {data.target} resolves to {data.resolvedIp}
        </div>
      )}

      {data?.mikrotik && can('access:ticket') && !can('tools:proxy') && <TempLoginPanel resourceId={resource.id} />}

      <Drawer open={editing} onClose={() => setEditing(false)} title={`Access ports — ${resource.name}`}>
        <AccessPortForm
          resource={resource}
          onSaved={() => {
            setEditing(false);
            onConfigured();
            reload();
          }}
        />
      </Drawer>

      <Drawer open={provisioning} onClose={() => setProvisioning(false)} title={`Set up a protocol — ${resource.name}`}>
        <ProvisionForm resourceId={resource.id} onDone={() => reload()} />
      </Drawer>

      <Drawer open={amiSetup} onClose={() => setAmiSetup(false)} title={`AMI manager — ${resource.name}`}>
        <AmiManagerForm
          resource={resource}
          onDone={() => {
            onConfigured();
            reload();
          }}
        />
      </Drawer>
    </div>
  );
}

/* ================================================================== */
/* Video source (cameras, DVRs, NVRs)                                 */
/* ================================================================== */

interface ResourceVideoData {
  resourceId: string;
  name: string;
  supported: boolean;
  vendor: string;
  channel: number;
  channelCount: number;
  rtspUrl: string | null;
  previewUrl: string | null;
  note: string | null;
}

/**
 * The generated RTSP source URL, for pasting into VLC or another real player,
 * plus a Preview button that polls a same-origin snapshot endpoint — see
 * `packages/server/src/services/video-access.ts` for why this is a
 * repeatedly-refreshed still frame rather than an embedded live stream: the
 * browser has no built-in way to play RTSP, and the vendor snapshot/MJPEG
 * CGIs need credentials the browser must never see directly.
 */
export function ResourceVideoCard({ resource }: { resource: Resource }) {
  const toast = useToast();
  const [previewing, setPreviewing] = useState(false);
  const { data, loading, error } = useFetch(
    () => api.get<ResourceVideoData>(`/api/tools/resource/${resource.id}/video`),
    [resource.id],
  );

  if (!loading && !data?.supported) return null;

  const copy = async () => {
    if (!data?.rtspUrl) return;
    if (await copyText(data.rtspUrl)) toast('Copied video source URL', 'good');
    else toast('Could not copy', 'bad', 'Your browser blocked clipboard access — select the URL instead.');
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Video size={14} />
        <span className="card-title">Video source</span>
        {data?.vendor && <Badge>{CAMERA_VENDOR_LABELS[data.vendor as keyof typeof CAMERA_VENDOR_LABELS] ?? data.vendor}</Badge>}
        <span className="grow" />
        {loading && <span className="spinner" />}
      </div>

      {error ? (
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>
          {error}
        </div>
      ) : !data?.rtspUrl ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          {data?.note ?? 'No IP configured.'}
        </div>
      ) : (
        <div className="card-body" style={{ display: 'grid', gap: 10 }}>
          <div className="row" style={{ gap: 6 }}>
            <code
              className="mono truncate"
              style={{ flex: 1, fontSize: 12.5, background: 'var(--itr-surface-2)', padding: '6px 8px', borderRadius: 6 }}
            >
              {data.rtspUrl}
            </code>
            <button className="btn btn-sm btn-icon" onClick={copy} title="Copy" aria-label="Copy video source URL">
              <Copy size={12} />
            </button>
          </div>
          {data.note && (
            <div className="subtle" style={{ fontSize: 11.5 }}>
              {data.note}
            </div>
          )}
          <div>
            <button className="btn btn-sm" onClick={() => setPreviewing(true)}>
              <PlayCircle size={13} /> Preview
            </button>
          </div>
        </div>
      )}

      <Drawer
        open={previewing}
        onClose={() => setPreviewing(false)}
        title={`Live preview — ${resource.name}`}
        wide={Boolean(data && data.channelCount > 1)}
      >
        {previewing && data && (
          <div style={{ display: 'grid', gap: 12 }}>
            {data.channelCount > 1 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                {Array.from({ length: data.channelCount }, (_, i) => i + 1).map((ch, i) => (
                  <div key={ch}>
                    <div className="subtle" style={{ fontSize: 11.5, marginBottom: 4 }}>
                      Channel {ch}
                    </div>
                    <CameraPreview resourceId={resource.id} channel={ch} intervalMs={3000} staggerMs={i * 250} />
                  </div>
                ))}
              </div>
            ) : (
              <CameraPreview resourceId={resource.id} />
            )}
            <div className="subtle" style={{ fontSize: 11.5 }}>
              Refreshing snapshots, not a real-time stream — for full video, open the source URL above in VLC or another RTSP player.
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/**
 * Polls the snapshot endpoint and swaps the `<img>` in as each frame lands.
 *
 * `intervalMs` defaults to a brisk 1.5s for a single camera; an NVR grid
 * passes a slower interval plus a per-tile stagger delay so previewing every
 * channel doesn't hit the device with N simultaneous requests every tick —
 * some NVR web servers choke on that.
 */
function CameraPreview({
  resourceId,
  channel,
  intervalMs = 1500,
  staggerMs = 0,
}: {
  resourceId: string;
  channel?: number;
  intervalMs?: number;
  staggerMs?: number;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const path = `/api/tools/resource/${resourceId}/video/preview${channel ? `?channel=${channel}` : ''}`;

    const tick = async () => {
      try {
        const next = await apiFetchBlobUrl(path);
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        const previous = objectUrlRef.current;
        objectUrlRef.current = next;
        setSrc(next);
        setError(null);
        if (previous) URL.revokeObjectURL(previous);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load preview');
      } finally {
        if (!cancelled) timer = setTimeout(tick, intervalMs);
      }
    };
    timer = setTimeout(tick, staggerMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [resourceId, channel, intervalMs, staggerMs]);

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {error ? (
        <div style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>{error}</div>
      ) : src ? (
        <img src={src} alt="Live preview" style={{ width: '100%', borderRadius: 8, background: '#000' }} />
      ) : (
        <div className="row" style={{ justifyContent: 'center', padding: 30 }}>
          <span className="spinner" />
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Configuration                                                      */
/* ================================================================== */

interface ResourceConfigData {
  configText: string | null;
  lineCount: number;
  fetchedAt: number | null;
  error: string | null;
}

/**
 * Cached RouterOS `/export`, pulled nightly across every site and shown here
 * verbatim (secrets already redacted server-side). This is what makes a
 * MikroTik's actual firewall/NAT/routing setup visible without opening
 * Winbox — the same cache the AI analyst reads from during an incident.
 */
export function ResourceConfigCard({ resource }: { resource: Resource }) {
  const { can } = useAuth();
  const toast = useToast();
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Starts minimised: a router's export is hundreds of lines, and the card
  // is opened to look something up, not read on every visit.
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = useFetch(
    () => api.get<ResourceConfigData>(`/api/resources/${resource.id}/config`),
    [resource.id],
  );

  // Hidden for anything that has never looked like a MikroTik and has no
  // cached config to show — no point offering an empty card for a Linux box.
  if (!isMikrotik(resource) && !data?.configText) return null;

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/api/resources/${resource.id}/config/refresh`, {});
      reload();
      toast('Configuration refreshed', 'good');
    } catch (err) {
      toast('Refresh failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <FileText size={14} />
        <span className="card-title">Configuration</span>
        {data?.fetchedAt && (
          <span className="card-subtitle">
            {formatRelative(data.fetchedAt)}
            {data.lineCount ? ` · ${data.lineCount} lines` : ''}
          </span>
        )}
        <span className="grow" />
        {loading && <span className="spinner" />}
        {can('resource:write') && (
          <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); void refresh(); }} disabled={refreshing}>
            <RefreshCw size={12} className={refreshing ? 'spin' : undefined} /> Refresh now
          </button>
        )}
        <button className="btn btn-sm btn-icon" title={open ? 'Minimise' : 'Show'} aria-label={open ? 'Minimise configuration' : 'Show configuration'} aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {!open ? null : error ? (
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>
          {error}
        </div>
      ) : data?.error ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          {data.error}
        </div>
      ) : !data?.configText ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          No configuration cached yet — pulled nightly from every MikroTik across every site, or refresh it now.
        </div>
      ) : (
        <div className="card-body">
          <pre
            className="mono"
            style={{
              fontSize: 11.5,
              lineHeight: 1.6,
              margin: 0,
              maxHeight: expanded ? 'none' : 260,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {data.configText}
          </pre>
          <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Collapse' : `Show all ${data.lineCount} lines`}
          </button>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Network discovery (MikroTik)                                       */
/* ================================================================== */

/**
 * Asks the router itself what's connected — its ARP table and DHCP leases —
 * rather than sweeping the subnet from this server, which only ever sees
 * whatever segment the server itself sits on. Anything new becomes a child
 * resource of this router with one click, so it shows up nested under it in
 * the network map.
 */
const NO_DEVICES: DiscoveredDevice[] = [];

/**
 * What the filter sees of a discovered device: its guessed type, whether the
 * router currently considers it live (the table's own Status column), and
 * every string the table shows.
 */
function describeDiscovered(d: DiscoveredDevice): DeviceFields {
  return {
    kind: d.suggestedKind?.kind ?? null,
    kindLabel: d.suggestedKind?.label ?? 'Unidentified',
    status: d.online ? 'online' : 'offline',
    statusLabel: d.online ? 'Online' : 'Offline',
    // Online first: a scan is read to find what is live right now.
    statusRank: d.online ? 0 : 1,
    text: [d.ip, d.previousAddress, d.hostname, d.vendor, d.model],
    macs: [d.mac],
  };
}

export function ResourceDiscoveryCard({ resource, onAdded }: { resource: Resource; onAdded: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const [devices, setDevices] = useState<DiscoveredDevice[] | null>(null);
  const [scanning, setScanning] = useState(false);
  const [probe, setProbe] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const keyOf = (d: DiscoveredDevice) => d.mac ?? d.ip;

  // Type, name, IP or MAC prefix — the same filter as the network map. The
  // header checkbox and the Add button act on the rows actually shown.
  const filter = useDeviceFilter(devices ?? NO_DEVICES, describeDiscovered);
  const visible = filter.visible;

  const scan = async () => {
    setScanning(true);
    setProbe(null);
    setError(null);
    setDevices(null);
    try {
      // The device list lands as soon as the router's tables are read; then
      // whatever they couldn't identify is probed one at a time, and each
      // result updates just its own row. A probe that finds nothing sends
      // nothing, so rows only ever gain detail.
      await streamNdjson<DiscoverEvent>(`/api/tools/resource/${resource.id}/discover`, {}, (event) => {
        if (event.type === 'devices') {
          setDevices(event.devices);
          setSelected(new Set(event.devices.filter((d) => !d.existingResourceId).map(keyOf)));
        } else if (event.type === 'update') {
          setDevices((current) => current?.map((d) => (keyOf(d) === event.key ? { ...d, ...event.patch } : d)) ?? current);
        } else if (event.type === 'progress') {
          setProbe(event.done >= event.total ? null : { done: event.done, total: event.total });
        } else if (event.type === 'error') {
          setError(event.error);
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setScanning(false);
      setProbe(null);
    }
  };

  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Header checkbox: every row, or none. Re-adding an "already added" row
  // only refreshes it in place, so selecting everything is safe.
  const allSelected = visible.length > 0 && visible.every((d) => selected.has(keyOf(d)));
  const toggleAll = () => {
    setSelected((current) => {
      const next = new Set(current);
      for (const d of visible) (allSelected ? next.delete(keyOf(d)) : next.add(keyOf(d)));
      return next;
    });
  };
  const chosen = visible.filter((d) => selected.has(keyOf(d)));

  const addSelected = async () => {
    if (!devices) return;
    if (chosen.length === 0) return;
    setAdding(true);
    try {
      const result = await api.post<{ added: number }>(`/api/tools/resource/${resource.id}/discover/apply`, {
        devices: chosen.map((d) => ({
          ip: d.ip,
          mac: d.mac,
          hostname: d.hostname,
          vendor: d.vendor,
          model: d.model,
          kind: d.suggestedKind?.kind ?? null,
        })),
      });
      toast(`Added ${result.added} device${result.added === 1 ? '' : 's'}`, 'good');
      onAdded();
      await scan();
    } catch (err) {
      toast('Could not add devices', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setAdding(false);
    }
  };

  if (!isMikrotik(resource) || !can('tools:run')) return null;

  const newCount = devices?.filter((d) => !d.existingResourceId).length ?? 0;
  const movedCount = devices?.filter((d) => d.previousAddress).length ?? 0;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Radar size={14} />
        <span className="card-title">Discover connected devices</span>
        {devices && <Badge intent={newCount ? 'warn' : 'good'}>{devices.length} found · {newCount} new</Badge>}
        {movedCount > 0 && (
          <span title="Known devices the router now sees at a different address — their resources were updated to follow them.">
            <Badge intent="warn">
              {movedCount} address{movedCount === 1 ? '' : 'es'} updated
            </Badge>
          </span>
        )}
        {probe && (
          <span className="subtle" style={{ fontSize: 12 }}>
            Identifying {probe.done} / {probe.total}…
          </span>
        )}
        <span className="grow" />
        <button className="btn btn-sm" onClick={scan} disabled={scanning}>
          {scanning ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Radar size={12} />}
          {scanning ? 'Scanning…' : devices ? 'Rescan' : 'Scan router'}
        </button>
      </div>

      {!devices && !scanning && !error && (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          Reads this router's own ARP table, DHCP leases and neighbour table to find what's connected to it — VLANs
          and wireless clients included, not just this server's own local segment — then asks anything unidentified,
          one device at a time, what it is.
        </div>
      )}

      {error && (
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>
          {error}
        </div>
      )}

      {devices && devices.length === 0 && (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          Nothing found — the ARP table and DHCP leases are both empty.
        </div>
      )}

      {devices && devices.length > 0 && (
        <>
          <DeviceFilterBar filter={filter} total={devices.length} placeholder="Hostname, vendor, IP or MAC prefix…" />
          {filter.active && visible.length === 0 && (
            <div className="card-body subtle" style={{ fontSize: 12.5 }}>
              No devices match the filter.
            </div>
          )}
          <div className="table-wrap" style={{ marginTop: 10 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && chosen.length > 0;
                      }}
                      onChange={toggleAll}
                      disabled={!can('resource:write')}
                      aria-label={allSelected ? 'Deselect all devices' : 'Select all devices'}
                      title={allSelected ? 'Deselect all' : 'Select all'}
                    />
                  </th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 130 }}>Address</th>
                  <th style={{ width: 150 }}>MAC</th>
                  <th>Name / vendor / model</th>
                  <th style={{ width: 140 }}>Suggested type</th>
                  <th style={{ width: 100 }}>Source</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => {
                  const key = keyOf(d);
                  return (
                    <tr key={key}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggle(key)}
                          disabled={!can('resource:write')}
                          aria-label={`Select ${d.ip}`}
                        />
                      </td>
                      <td>
                        <Badge intent={d.online ? 'good' : 'neutral'}>{d.online ? 'Online' : 'Offline'}</Badge>
                      </td>
                      <td className="mono">
                        {d.ip}
                        {d.previousAddress && (
                          <div className="subtle" style={{ fontSize: 11 }} title={`Was ${d.previousAddress} — the resource now points at ${d.ip}`}>
                            was {d.previousAddress}
                          </div>
                        )}
                      </td>
                      <td className="mono subtle">{d.mac ?? '—'}</td>
                      <td className="truncate" style={{ maxWidth: 240 }}>
                        {d.hostname ? (
                          d.hostname
                        ) : (
                          <span className="subtle" title={d.macRandomized ? 'A private/randomised Wi-Fi address — phones and laptops generate a fresh one per network by default, so it never maps to a manufacturer.' : undefined}>
                            {d.vendor ?? (d.macRandomized ? 'Randomized MAC' : 'Unknown')}
                          </span>
                        )}
                        {d.hostname && (d.vendor || d.macRandomized) && (
                          <div className="subtle truncate" style={{ fontSize: 11 }}>
                            {d.vendor ?? 'Randomized MAC'}
                          </div>
                        )}
                        {d.model && (
                          <div className="subtle truncate" style={{ fontSize: 11 }} title={d.model}>
                            {d.model}
                          </div>
                        )}
                      </td>
                      <td>
                        {d.suggestedKind ? <Badge intent="good">{d.suggestedKind.label}</Badge> : <span className="subtle">Unidentified</span>}
                        {d.reason && (
                          <div className="subtle truncate" style={{ fontSize: 11, maxWidth: 200 }} title={d.reason}>
                            {d.reason}
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge>{d.via === 'dhcp' ? 'DHCP lease' : 'ARP'}</Badge>
                      </td>
                      <td>
                        {d.existingResourceId ? (
                          <Link to={`/resources/${d.existingResourceId}`} style={{ fontSize: 12 }}>
                            Already added
                          </Link>
                        ) : (
                          <span className="subtle" style={{ fontSize: 12 }}>
                            New
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {can('resource:write') && (
            <div className="card-body" style={{ paddingTop: 0 }}>
              <button className="btn btn-primary btn-sm" onClick={addSelected} disabled={adding || chosen.length === 0}>
                <Plus size={12} /> {adding ? 'Adding…' : `Add ${chosen.length} device${chosen.length === 1 ? '' : 's'} as resources`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================================================================== */
/* Torch (MikroTik)                                                   */
/* ================================================================== */

interface TorchResponse {
  interfaceName: string;
  durationSec: number;
  flows: TorchFlow[];
}

/**
 * RouterOS's own live traffic monitor, run through the server: which flows
 * are actually using an interface right now, broken down by source,
 * destination and protocol. Unlike Winbox's continuously refreshing view,
 * this captures for a fixed window and shows one aggregated reading — see
 * mikrotik-torch.ts on the server for why.
 */
export function ResourceTorchCard({ resource }: { resource: Resource }) {
  const { can } = useAuth();
  const [interfaces, setInterfaces] = useState<string[] | null>(null);
  const [loadingInterfaces, setLoadingInterfaces] = useState(false);
  const [iface, setIface] = useState('');
  const [duration, setDuration] = useState(5);
  const [result, setResult] = useState<TorchResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInterfaces = async () => {
    setLoadingInterfaces(true);
    setError(null);
    try {
      const res = await api.get<{ interfaces: string[] }>(`/api/tools/resource/${resource.id}/torch/interfaces`);
      setInterfaces(res.interfaces);
      setIface((current) => current || res.interfaces[0] || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingInterfaces(false);
    }
  };

  const eligible = isMikrotik(resource) && can('tools:run');

  useEffect(() => {
    if (eligible) void loadInterfaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.id, eligible]);

  const run = async () => {
    if (!iface) return;
    setRunning(true);
    setError(null);
    try {
      const res = await api.post<TorchResponse>(`/api/tools/resource/${resource.id}/torch`, { interface: iface, durationSec: duration });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  if (!eligible) return null;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Flashlight size={14} />
        <span className="card-title">Torch</span>
        {result && (
          <span className="card-subtitle">
            {result.flows.length} flow{result.flows.length === 1 ? '' : 's'} on {result.interfaceName}
          </span>
        )}
        <span className="grow" />
        {(running || loadingInterfaces) && <span className="spinner" />}
      </div>

      <div className="card-body">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <div>
            <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
              Interface
            </label>
            <select
              className="select"
              style={{ width: 200 }}
              value={iface}
              onChange={(e) => setIface(e.target.value)}
              disabled={loadingInterfaces || !interfaces?.length}
            >
              {(interfaces ?? []).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
              Duration (s)
            </label>
            <input
              className="input"
              type="number"
              min={1}
              max={30}
              style={{ width: 90 }}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={run} disabled={running || loadingInterfaces || !iface}>
            {running ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Play size={12} />}
            {running ? 'Capturing…' : 'Run'}
          </button>
          <button className="btn btn-sm btn-icon" onClick={loadInterfaces} disabled={loadingInterfaces} title="Reload interfaces">
            <RefreshCw size={12} className={loadingInterfaces ? 'spin' : undefined} />
          </button>
        </div>

        {!result && !running && !error && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            Captures live traffic on the chosen interface for the set duration and breaks it down by source,
            destination and protocol — RouterOS's own Torch tool, run through the server.
          </div>
        )}

        {error && (
          <div style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>{error}</div>
        )}

        {result && result.flows.length === 0 && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            No traffic seen on {result.interfaceName} during the {result.durationSec}s capture.
          </div>
        )}
      </div>

      {result && result.flows.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 420 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Destination</th>
                <th style={{ width: 100 }}>Protocol</th>
                <th data-align="right" style={{ width: 90 }}>
                  TX
                </th>
                <th data-align="right" style={{ width: 90 }}>
                  RX
                </th>
              </tr>
            </thead>
            <tbody>
              {result.flows.map((flow, i) => (
                <tr key={i}>
                  <td className="mono">
                    {flow.srcAddress ?? '—'}
                    {flow.srcPort ? `:${flow.srcPort}` : ''}
                  </td>
                  <td className="mono">
                    {flow.dstAddress ?? '—'}
                    {flow.dstPort ? `:${flow.dstPort}` : ''}
                  </td>
                  <td>
                    <span className="row" style={{ gap: 6 }}>
                      {flow.protocol ? <Badge>{flow.protocol}</Badge> : <span className="subtle">—</span>}
                      {flow.service && (
                        <span className="subtle" style={{ fontSize: 11 }}>
                          {flow.service}
                        </span>
                      )}
                    </span>
                  </td>
                  <td data-align="right" className="nums">
                    {formatBytes(flow.txBytes)}
                  </td>
                  <td data-align="right" className="nums">
                    {formatBytes(flow.rxBytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const USERS_REFRESH_MS = 15_000;

/**
 * Every user behind the router — DHCP leases, ARP entries, hotspot logins —
 * and the internet speed each is using right now, measured on the router
 * over a few seconds (see mikrotik-users.ts). Refreshes itself while open.
 */
export function ResourceMikrotikUsersCard({ resource }: { resource: Resource }) {
  const { can } = useAuth();
  const eligible = isMikrotik(resource) && can('tools:run');
  const [result, setResult] = useState<MikrotikUsersResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(true);
  const [filter, setFilter] = useState('');
  const busy = useRef(false);

  const load = async () => {
    // A slow router can take longer than the refresh interval; never stack reads.
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    try {
      setResult(await api.get<MikrotikUsersResult>(`/api/tools/resource/${resource.id}/mikrotik/users`));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      busy.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!eligible) return;
    setResult(null);
    void load();
    if (!live) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, USERS_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.id, eligible, live]);

  if (!eligible) return null;

  const needle = filter.trim().toLowerCase();
  const users = (result?.users ?? []).filter(
    (u) => !needle || [u.name, u.hostName, u.address, u.mac, u.vendor].some((v) => v?.toLowerCase().includes(needle)),
  );
  const active = (result?.users ?? []).filter((u) => u.downloadBps + u.uploadBps > 0).length;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Users size={14} />
        <span className="card-title">Users &amp; internet use</span>
        {result && (
          <span className="card-subtitle">
            {result.users.length} user{result.users.length === 1 ? '' : 's'} · {active} using the internet · {formatRelative(result.ts)}
          </span>
        )}
        <span className="grow" />
        {loading && <span className="spinner" />}
        <label className="row subtle" style={{ gap: 5, fontSize: 12, marginLeft: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
          Live
        </label>
        <button className="btn btn-sm btn-icon" onClick={() => void load()} disabled={loading} title="Read again now">
          <RefreshCw size={12} className={loading ? 'spin' : undefined} />
        </button>
      </div>

      <div className="card-body">
        {result && (
          <div className="row" style={{ gap: 18, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontSize: 12.5 }}>
            <span>
              <span className="subtle">Users download </span>
              <strong className="nums">{formatBitsPerSec(result.totalDownloadBps)}</strong>
              <span className="subtle"> · upload </span>
              <strong className="nums">{formatBitsPerSec(result.totalUploadBps)}</strong>
            </span>
            {result.wanDownloadBps !== null && (
              <span title={`Read off ${result.wanInterfaces.join(', ')} over the same seconds`}>
                <span className="subtle">Internet lines in </span>
                <span className="nums">{formatBitsPerSec(result.wanDownloadBps)}</span>
                <span className="subtle"> · out </span>
                <span className="nums">{formatBitsPerSec(result.wanUploadBps ?? 0)}</span>
              </span>
            )}
            <span className="grow" />
            <input
              className="input"
              style={{ width: 220 }}
              placeholder="Filter by name, IP or MAC"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        )}

        {!result && !error && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            Reading the router's users and measuring their traffic for a few seconds…
          </div>
        )}
        {error && <div style={{ fontSize: 12.5, color: 'var(--itr-critical)' }}>{error}</div>}
        {result && result.users.length === 0 && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            No users found on {result.lanNetworks.join(', ') || 'the LAN side'}.
          </div>
        )}
      </div>

      {users.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 520 }}>
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th style={{ width: 130 }}>IP</th>
                <th style={{ width: 170 }}>MAC</th>
                <th data-align="right" style={{ width: 110 }}>
                  Download
                </th>
                <th data-align="right" style={{ width: 110 }}>
                  Upload
                </th>
                <th data-align="right" style={{ width: 90 }} title="Open internet connections">
                  Conns
                </th>
                <th data-align="right" style={{ width: 150 }} title="Hotspot session total (down / up)">
                  Session
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const idle = u.downloadBps + u.uploadBps === 0;
                return (
                  <tr key={u.address} style={idle ? { opacity: 0.6 } : undefined}>
                    <td>
                      <div>{u.name ?? u.hostName ?? <span className="subtle">unnamed</span>}</div>
                      <div className="subtle" style={{ fontSize: 11 }}>
                        {[u.name && u.hostName !== u.name ? u.hostName : null, u.vendor, u.sources.includes('hotspot') ? 'hotspot' : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td className="mono">{u.address}</td>
                    <td className="mono">{u.mac ?? <span className="subtle">—</span>}</td>
                    <td data-align="right" className="nums">
                      {formatBitsPerSec(u.downloadBps)}
                    </td>
                    <td data-align="right" className="nums">
                      {formatBitsPerSec(u.uploadBps)}
                    </td>
                    <td data-align="right" className="nums">
                      {u.connections}
                    </td>
                    <td data-align="right" className="nums subtle">
                      {u.sessionDownloadBytes === null
                        ? '—'
                        : `${formatBytes(u.sessionDownloadBytes)} / ${formatBytes(u.sessionUploadBytes)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatBytes(value: number | null): string {
  if (value === null) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface TempLogin {
  username: string;
  password: string;
  createdAt: number;
  ticketReference: string | null;
}

/**
 * Winbox and WebFig can't have a login filled in by the server, so a
 * ticket-scoped technician gets a router account of their own instead of the
 * real admin password. It can change config but not users or secrets, and it
 * is removed from the router when the ticket is resolved or reassigned.
 */
function TempLoginPanel({ resourceId }: { resourceId: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { data, error, reload } = useFetch(
    () => api.get<{ account: TempLogin | null }>(`/api/tools/resource/${resourceId}/temp-account`),
    [resourceId],
  );
  const account = data?.account ?? null;

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    try {
      await fn();
      reload();
    } catch (err) {
      toast(failure, 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (label: string, value: string) => {
    if (await copyText(value)) toast(`Copied ${label}`, 'good');
    else toast('Could not copy', 'bad', 'Your browser blocked clipboard access — select the text instead.');
  };

  return (
    <div className="card-body" style={{ borderTop: 'var(--itr-border-w) solid var(--itr-border)', fontSize: 12.5 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <ShieldCheck size={14} />
        <strong>Temporary login for Winbox and WebFig</strong>
      </div>
      {error ? (
        <div className="subtle">{error}</div>
      ) : account ? (
        <>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              User <span className="mono">{account.username}</span>{' '}
              <button className="btn btn-sm" onClick={() => void copy('username', account.username)}>
                Copy
              </button>
            </span>
            <span>
              Password <span className="mono">{account.password}</span>{' '}
              <button className="btn btn-sm" onClick={() => void copy('password', account.password)}>
                Copy
              </button>
            </span>
            <span className="grow" />
            <button className="btn btn-sm" disabled={busy} onClick={() => void act(() => api.delete(`/api/tools/resource/${resourceId}/temp-account`), 'Could not remove the login')}>
              Remove now
            </button>
          </div>
          <div className="subtle" style={{ marginTop: 6, fontSize: 11.5 }}>
            Yours alone{account.ticketReference ? ` for ticket ${account.ticketReference}` : ''}. It can change configuration but not users or
            secrets, and it is removed from the router when the ticket is resolved, closed or reassigned.
          </div>
        </>
      ) : (
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <span className="subtle">
            Get a login of your own on this router — the real admin password is never shown. Removed automatically when your ticket ends.
          </span>
          <span className="grow" />
          <button
            className="btn btn-sm btn-primary"
            disabled={busy}
            onClick={() => void act(() => api.post(`/api/tools/resource/${resourceId}/temp-account`), 'Could not create the login')}
          >
            {busy ? 'Creating…' : 'Create temporary login'}
          </button>
        </div>
      )}
    </div>
  );
}

function AccessRow({ resourceId, endpoint }: { resourceId: string; endpoint: AccessEndpoint }) {
  const { can } = useAuth();
  const toast = useToast();
  const address = `${endpoint.host}:${endpoint.port}`;
  const [opening, setOpening] = useState(false);
  const [tunnel, setTunnel] = useState<{ id: string; address: string; url: string | null; expiresAt: number; hasWindow: boolean } | null>(
    null,
  );
  // The tab the device UI was opened in, so its closing can free the port.
  // Only meaningful when `tunnel.hasWindow` — ssh/telnet/winbox have no
  // browser-openable URL, so nothing is ever opened for them to watch.
  const openedWindow = useRef<Window | null>(null);

  const copy = async () => {
    if (await copyText(address)) toast(`Copied ${address}`, 'good');
    else toast('Could not copy', 'bad', 'Your browser blocked clipboard access — select the address instead.');
  };

  // Hand the port back as soon as the device tab goes away, rather than
  // holding it until the server's idle timer notices. Polling `closed` is the
  // only signal available — the tunnel tab is a different origin, so there is
  // nothing to listen to and no reason for it to cooperate.
  //
  // Deliberately not closed on unmount or on this page's own unload: an
  // operator navigating the dashboard elsewhere, or closing the dashboard tab
  // while still working in the device tab, must not have the device yanked out
  // from under them. Those cases fall through to the server's idle timeout and
  // absolute lifetime cap, which is what actually guarantees the port is freed.
  const tunnelId = tunnel?.hasWindow ? tunnel.id : null;
  useEffect(() => {
    if (!tunnelId) return;
    const timer = setInterval(() => {
      if (!openedWindow.current?.closed) return;
      openedWindow.current = null;
      setTunnel(null);
      void api.delete(`/api/tools/resource/${resourceId}/tunnel/${tunnelId}`).catch(() => {
        /* already reaped by the idle timer, or the server restarted */
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [resourceId, tunnelId]);

  // A plain <a href target="_blank"> never carries our Authorization header —
  // browsers only attach that from JS — so the proxied page and everything it
  // loads (css, js, images) would 401. Mint a resource-scoped cookie ticket
  // first, through a normal authenticated request, then navigate.
  const openViaServer = async () => {
    setOpening(true);
    const win = window.open('', '_blank');
    try {
      // Pin the exact row clicked (http vs. https) — without it both rows
      // proxied through the same guessed admin URL, so the http row silently
      // tried https:443 too and failed on devices serving plain HTTP only.
      const { url } = await api.post<{ url: string }>(`/api/tools/proxy/${resourceId}/session`, {
        scheme: endpoint.service === 'https' ? 'https' : 'http',
        port: endpoint.port,
      });
      // Absolute when the proxy has its own origin; a same-origin path otherwise.
      if (win) win.location.href = /^https?:\/\//.test(url) ? url : apiUrl(url);
      else toast('Pop-up blocked', 'bad', 'Allow pop-ups for this site to open the device UI in a new tab.');
    } catch (err) {
      win?.close();
      toast('Could not open', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setOpening(false);
    }
  };

  /**
   * Port-forward instead of proxying: the browser connects to
   * `<server>:<port>`, which is relayed straight to the device.
   *
   * The difference that matters is the origin. "Via server" serves the device
   * under our own hostname beneath `/api/tools/proxy/:id/`, so every URL the
   * device emits has to be rewritten to stay inside that prefix — and any URL
   * built in a way the rewriter doesn't recognise escapes to the dashboard
   * instead (RouterOS webfig's `location.replace(`/${hash}`)` did exactly
   * that). Here the origin *is* the device, so its own root-relative URLs are
   * already correct and nothing is rewritten at all.
   */
  const openViaPort = async () => {
    setOpening(true);
    const win = window.open('', '_blank');
    try {
      const opened = await api.post<{ id: string; host: string; port: number; url: string | null; expiresAt: number }>(
        `/api/tools/resource/${resourceId}/tunnel`,
        { service: endpoint.service, port: endpoint.port },
      );
      if (win && opened.url) {
        win.location.href = opened.url;
        openedWindow.current = win;
      } else {
        win?.close();
        // The tunnel is open regardless — show the URL rather than throwing
        // away a port the operator can still use by hand.
        toast('Pop-up blocked', 'bad', 'Allow pop-ups, or use the link now shown on this row.');
      }
      setTunnel({ id: opened.id, address: `${opened.host}:${opened.port}`, url: opened.url, expiresAt: opened.expiresAt, hasWindow: true });
    } catch (err) {
      win?.close();
      toast('Could not open a tunnel', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setOpening(false);
    }
  };

  const openWinboxTunnel = async () => {
    setOpening(true);
    try {
      const opened = await api.post<{ id: string; host: string; port: number; url: string | null; expiresAt: number }>(
        `/api/tools/resource/${resourceId}/tunnel`,
        { service: 'winbox', port: endpoint.port },
      );
      setTunnel({ id: opened.id, address: `${opened.host}:${opened.port}`, url: opened.url, expiresAt: opened.expiresAt, hasWindow: false });
    } catch (err) {
      toast('Could not open a tunnel', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setOpening(false);
    }
  };

  const closeTunnel = async () => {
    if (!tunnel) return;
    const { id } = tunnel;
    setTunnel(null);
    openedWindow.current?.close();
    openedWindow.current = null;
    try {
      await api.delete(`/api/tools/resource/${resourceId}/tunnel/${id}`);
      toast('Tunnel closed', 'good');
    } catch {
      /* already gone — nothing to report */
    }
  };

  return (
    <tr>
      <td>
        <span style={{ fontWeight: 550 }}>{endpoint.label}</span>
        {(endpoint.custom || endpoint.addressLabel) && (
          <div className="subtle" style={{ fontSize: 11 }}>
            {[endpoint.addressLabel, endpoint.custom ? 'custom port' : null].filter(Boolean).join(' · ')}
          </div>
        )}
        {tunnel && !tunnel.hasWindow && (
          <div className="subtle" style={{ fontSize: 11 }}>
            Point Winbox at <span className="mono">{tunnel.address}</span>{' '}
            ·{' '}
            <button
              type="button"
              onClick={closeTunnel}
              style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            >
              close now
            </button>
          </div>
        )}
        {tunnel && tunnel.hasWindow && tunnel.url && (
          <div className="subtle" style={{ fontSize: 11 }}>
            <a className="mono" href={tunnel.url} target="_blank" rel="noreferrer noopener">
              {tunnel.url}
            </a>{' '}
            ·{' '}
            <button
              type="button"
              onClick={closeTunnel}
              style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            >
              close now
            </button>
          </div>
        )}
      </td>
      <td className="mono subtle">{address}</td>
      <td>
        {endpoint.open === null ? (
          <span className="subtle">not checked</span>
        ) : endpoint.open ? (
          <span className="row" style={{ gap: 6 }}>
            <Badge intent="good">open</Badge>
            {endpoint.transport && <Badge>{endpoint.transport.toUpperCase()}</Badge>}
            {endpoint.connectMs !== null && <span className="subtle nums" style={{ fontSize: 11.5 }}>{endpoint.connectMs} ms</span>}
          </span>
        ) : (
          // Not an error: a closed telnet port is the correct answer, and a
          // closed SSH port on a device you can otherwise reach is exactly the
          // fact the operator came here for.
          <Badge>closed</Badge>
        )}
      </td>
      <td>
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          {endpoint.service !== 'sip' && (
          <a
            className="btn btn-sm"
            href={endpoint.uri}
            // Always a new tab/window, even for a non-standard scheme like
            // winbox:// — with no target, a browser that has no handler for
            // it navigates the *current* tab to a dead end instead of just
            // quietly doing nothing, which reads as "the dashboard broke".
            target="_blank"
            rel="noreferrer noopener"
            title={endpoint.standardScheme ? endpoint.uri : `${endpoint.help}\n\n${endpoint.uri}`}
          >
            {endpoint.proxyable ? <ExternalLink size={12} /> : <Terminal size={12} />}
            {endpoint.proxyable ? 'Open' : 'Launch'}
          </a>
          )}
          {endpoint.proxyable && (can('tools:proxy') || can('access:ticket')) && (
            <button
              className="btn btn-sm"
              onClick={openViaServer}
              disabled={opening}
              title="Reach this device's web UI through the server, for networks you cannot route to directly."
            >
              {opening ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null}
              Via server
            </button>
          )}
          {(endpoint.service === 'http' || endpoint.service === 'https') && (can('tools:proxy') || can('access:ticket')) && (
            <button
              className="btn btn-sm"
              onClick={openViaPort}
              disabled={opening}
              title={
                'Forward a temporary port on the server straight to this device, and open it.\n\n' +
                'Nothing is rewritten, so device UIs that "Via server" breaks (RouterOS webfig) work normally. ' +
                'The port closes when you close the tab, after 10 minutes idle, or after an hour.'
              }
            >
              {opening ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null}
              Via port
            </button>
          )}
          {endpoint.service === 'winbox' && (can('tools:proxy') || can('access:ticket')) && (
            <button
              className="btn btn-sm"
              onClick={openWinboxTunnel}
              disabled={opening}
              title="Open a short-lived TCP relay so your own desktop Winbox can reach this device through the server."
            >
              {opening ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null}
              Via server
            </button>
          )}
          {(endpoint.service === 'ssh' || endpoint.service === 'telnet') && (can('tools:proxy') || can('access:ticket')) && (
            <a
              className="btn btn-sm"
              href={`/resources/${resourceId}/terminal/${endpoint.service}`}
              target="_blank"
              rel="noreferrer noopener"
              title="Open a terminal to this device through the server, for networks you cannot route to directly."
            >
              <Terminal size={12} /> Via server
            </a>
          )}
          <button className="btn btn-sm btn-icon" onClick={copy} title={`Copy ${address}`} aria-label={`Copy ${address}`}>
            <Copy size={12} />
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Clipboard, including the case that matters here: the portal is often served
 * over plain HTTP on a LAN, where `navigator.clipboard` does not exist.
 */
async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }

  try {
    const field = document.createElement('textarea');
    field.value = value;
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}

interface SavedCredential {
  id: string;
  username: string | null;
  type: string;
  hasSecret: boolean;
}

const PROVISION_LABELS: Record<ProvisionProtocol, string> = { snmp: 'SNMP', ssh: 'SSH', telnet: 'Telnet' };

/**
 * Logs into the device and turns on a protocol — enables the service, adds a
 * firewall accept for this server, and for SNMP sets the community.
 *
 * Preview-then-apply on purpose: the exact RouterOS commands are shown before
 * anything runs, since this is the one action on this page that writes to the
 * device rather than just reading it.
 */
function ProvisionForm({ resourceId, onDone }: { resourceId: string; onDone: () => void }) {
  const toast = useToast();
  const [protocol, setProtocol] = useState<ProvisionProtocol>('snmp');
  const [transport, setTransport] = useState<'ssh' | 'telnet'>('ssh');
  const [vaultEntryId, setVaultEntryId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [community, setCommunity] = useState('public');
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [result, setResult] = useState<ProvisionResult | null>(null);

  const { data: saved } = useFetch(() => api.get<SavedCredential[]>('/api/vault/entries', { resourceId }), [resourceId]);
  const credentialEntries = (saved ?? []).filter((e) => e.type === 'password' && e.hasSecret && e.username);

  // A stale preview must not be Apply-able once the operator changes what it
  // would even run — "apply" always uses current field values, so the two
  // have to be kept in lockstep.
  useEffect(() => {
    setResult(null);
  }, [protocol, transport, vaultEntryId, username, password, community]);

  const run = async (apply: boolean) => {
    setBusy(apply ? 'apply' : 'preview');
    setResult(null);
    try {
      const body: Record<string, unknown> = { protocol, transport, apply };
      if (vaultEntryId) body.vaultEntryId = vaultEntryId;
      else {
        body.username = username;
        body.password = password;
      }
      if (protocol === 'snmp') body.community = community;

      const res = await api.post<ProvisionResult>(`/api/tools/resource/${resourceId}/provision`, body);
      setResult(res);
      if (apply) {
        const failed = res.steps.some((s) => s.ok === false);
        toast(failed ? 'Stopped partway — see the step that failed below' : `${PROVISION_LABELS[protocol]} enabled`, failed ? 'bad' : 'good');
        if (!failed) onDone();
      }
    } catch (err) {
      toast('Could not reach the device', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  const haveCredentials = vaultEntryId || (username && password);

  return (
    <div>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
        Logs into the device over {transport.toUpperCase()} and runs RouterOS commands: enables the service, and adds a
        firewall rule that lets this server reach it. Never edits or removes an existing rule.
      </p>

      <div className="field">
        <label className="field-label">Protocol to enable</label>
        <select className="select" value={protocol} onChange={(e) => setProtocol(e.target.value as ProvisionProtocol)}>
          <option value="snmp">SNMP (for monitoring)</option>
          <option value="ssh">SSH</option>
          <option value="telnet">Telnet</option>
        </select>
      </div>

      {protocol === 'snmp' && (
        <div className="field">
          <label className="field-label">SNMP community</label>
          <input className="input" value={community} onChange={(e) => setCommunity(e.target.value)} placeholder="public" />
        </div>
      )}

      <div className="field">
        <label className="field-label">Log in via</label>
        <select className="select" value={transport} onChange={(e) => setTransport(e.target.value as 'ssh' | 'telnet')}>
          <option value="ssh">SSH</option>
          <option value="telnet">Telnet</option>
        </select>
      </div>

      {credentialEntries.length > 0 && (
        <div className="field">
          <label className="field-label">Credential</label>
          <select
            className="select"
            value={vaultEntryId}
            onChange={(e) => {
              setVaultEntryId(e.target.value);
              if (e.target.value) setPassword('');
            }}
          >
            <option value="">Enter manually…</option>
            {credentialEntries.map((e) => (
              <option key={e.id} value={e.id}>
                Saved: {e.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {!vaultEntryId && (
        <div className="row" style={{ gap: 8 }}>
          <div className="field grow">
            <label className="field-label">Username</label>
            <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
          </div>
          <div className="field grow">
            <label className="field-label">Password</label>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
          </div>
        </div>
      )}

      <div className="row" style={{ gap: 8, marginTop: 4, marginBottom: 16 }}>
        <button type="button" className="btn grow" disabled={!haveCredentials || busy !== null} onClick={() => run(false)}>
          {busy === 'preview' ? 'Checking…' : 'Preview commands'}
        </button>
        <button
          type="button"
          className="btn btn-primary grow"
          disabled={!haveCredentials || busy !== null || !result}
          onClick={() => run(true)}
        >
          {busy === 'apply' ? 'Applying…' : 'Apply'}
        </button>
      </div>

      {result && (
        <div className="col" style={{ gap: 8 }}>
          {!result.applied && (
            <div className="subtle" style={{ fontSize: 12 }}>
              Source address this server was seen from: <span className="mono">{result.sourceAddress}</span>. Preview
              only — nothing has run yet.
            </div>
          )}
          <ProvisionSteps steps={result.steps} />
        </div>
      )}
    </div>
  );
}

/**
 * The command list, shared by both setup flows.
 *
 * Rendered as pre-wrapped monospace because the Asterisk steps are whole `sh`
 * scripts rather than one-liners — the point of the preview is that an
 * operator can read what will run before it runs, which a single wrapped line
 * of shell defeats.
 */
function ProvisionSteps({ steps }: { steps: Array<ProvisionResult['steps'][number]> }) {
  return (
    <>
      {steps.map((step, i) => (
        <div
          key={i}
          className="card"
          style={{
            padding: '9px 11px',
            borderLeft:
              step.ok === false ? '3px solid var(--itr-critical)' : step.ok === true ? '3px solid var(--itr-success)' : undefined,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 550, marginBottom: 4 }}>{step.title}</div>
          <div className="mono subtle" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {step.command}
          </div>
          {step.output !== undefined && (
            <div
              className="mono"
              style={{
                fontSize: 11,
                marginTop: 4,
                whiteSpace: 'pre-wrap',
                color: step.ok === false ? 'var(--itr-critical)' : undefined,
              }}
            >
              {step.error ?? step.output ?? '(no output)'}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * Sets up the Asterisk Manager Interface on a PBX over SSH: creates the
 * manager user in its own include file, restricts it to this server, binds the
 * manager to 0.0.0.0, opens the host firewall, reloads, then logs in over AMI
 * to prove it — and on success saves the credentials onto the resource so the
 * VOIP collector picks them up on its next poll.
 *
 * Preview-then-apply like the MikroTik form, and for a stronger reason: one of
 * these steps rewrites three keys in the PBX's own `manager.conf`.
 */
function AmiManagerForm({ resource, onDone }: { resource: Resource; onDone: () => void }) {
  const toast = useToast();
  const [vaultEntryId, setVaultEntryId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [amiUsername, setAmiUsername] = useState(String(resource.config.amiUsername ?? '') || 'itr-monitor');
  const [amiSecret, setAmiSecret] = useState('');
  const [amiPort, setAmiPort] = useState(String(resource.config.amiPort ?? 5038));
  // Every address the PBX is registered under, plus a saved AMI host that is
  // not (a VPN address somebody typed into the config directly).
  const savedAmiHost = String(resource.config.amiHost ?? '').trim();
  const amiHosts = [...new Set([...registeredHosts(resource), ...(savedAmiHost ? [savedAmiHost] : [])])];
  const [amiHost, setAmiHost] = useState(savedAmiHost || amiHosts[0] || '');
  const [bindAddress, setBindAddress] = useState('0.0.0.0');
  const [sourceAddress, setSourceAddress] = useState('');
  const [openFirewall, setOpenFirewall] = useState(true);
  const [saveCredentials, setSaveCredentials] = useState(true);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [result, setResult] = useState<AmiProvisionResult | null>(null);

  const { data: saved } = useFetch(
    () => api.get<SavedCredential[]>('/api/vault/entries', { resourceId: resource.id }),
    [resource.id],
  );
  const credentialEntries = (saved ?? []).filter((e) => e.type === 'password' && e.hasSecret && e.username);

  // Same lockstep rule as the MikroTik form: Apply always sends current field
  // values, so a preview must not outlive the fields it was built from.
  useEffect(() => {
    setResult(null);
  }, [vaultEntryId, username, password, amiUsername, amiSecret, amiPort, amiHost, bindAddress, sourceAddress, openFirewall]);

  const run = async (apply: boolean) => {
    setBusy(apply ? 'apply' : 'preview');
    setResult(null);
    try {
      const body: Record<string, unknown> = {
        amiUsername: amiUsername.trim(),
        amiPort: Number(amiPort) || 5038,
        bindAddress: bindAddress.trim() || '0.0.0.0',
        ...(amiHost ? { amiHost } : {}),
        openFirewall,
        saveCredentials,
        apply,
      };
      if (vaultEntryId) body.vaultEntryId = vaultEntryId;
      else if (username) {
        body.username = username;
        body.password = password;
      }
      if (amiSecret.trim()) body.amiSecret = amiSecret.trim();
      if (sourceAddress.trim()) body.sourceAddress = sourceAddress.trim();

      const res = await api.post<AmiProvisionResult>(`/api/tools/resource/${resource.id}/ami-manager`, body);
      setResult(res);
      if (apply) {
        const failed = res.steps.some((s) => s.ok === false);
        if (failed) toast('Stopped partway — see the step that failed below', 'bad');
        else if (res.verified) {
          toast('AMI manager ready', 'good', res.credentialsSaved ? 'Credentials saved to this resource.' : undefined);
          onDone();
        } else {
          toast('Config written, but the AMI login still failed', 'bad', res.verifyMessage ?? undefined);
        }
      }
    } catch (err) {
      toast('Could not reach the PBX', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
        Logs into the PBX over SSH and sets up AMI: writes the manager user to its own{' '}
        <span className="mono">manager_itr.conf</span> include, denies everything but this server, makes the manager
        listen on <span className="mono">{bindAddress || '0.0.0.0'}</span>, opens the host firewall, reloads, and then
        logs in over AMI to check it worked. <span className="mono">manager.conf</span> is backed up first — only{' '}
        <span className="mono">enabled</span>, <span className="mono">bindaddr</span> and{' '}
        <span className="mono">port</span> are rewritten in it.
      </p>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Manager username</label>
          <input className="input" value={amiUsername} onChange={(e) => setAmiUsername(e.target.value)} placeholder="itr-monitor" />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label className="field-label">AMI port</label>
          <input className="input" value={amiPort} onChange={(e) => setAmiPort(e.target.value)} inputMode="numeric" />
        </div>
      </div>

      {amiHosts.length > 1 && (
        <div className="field">
          <label className="field-label">Connect to AMI on</label>
          <select className="select" value={amiHost} onChange={(e) => setAmiHost(e.target.value)}>
            {amiHosts.map((h, i) => (
              <option key={h} value={h}>
                {h}
                {i === 0 ? ' (primary)' : ''}
              </option>
            ))}
          </select>
          <div className="subtle" style={{ fontSize: 11.5, marginTop: 4 }}>
            SSH goes to this address too, so the ACL admits the address the PBX sees this server on from there.
          </div>
        </div>
      )}

      <div className="field">
        <label className="field-label">Manager secret</label>
        <input
          className="input"
          value={amiSecret}
          onChange={(e) => setAmiSecret(e.target.value)}
          autoComplete="off"
          placeholder="Leave blank to generate one (or keep the saved one)"
        />
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Listen on</label>
          <input className="input" value={bindAddress} onChange={(e) => setBindAddress(e.target.value)} placeholder="0.0.0.0" />
        </div>
        <div className="field grow">
          <label className="field-label">Allow from</label>
          <input
            className="input"
            value={sourceAddress}
            onChange={(e) => setSourceAddress(e.target.value)}
            placeholder="Detected from the SSH session"
          />
        </div>
      </div>

      {credentialEntries.length > 0 && (
        <div className="field">
          <label className="field-label">SSH credential</label>
          <select
            className="select"
            value={vaultEntryId}
            onChange={(e) => {
              setVaultEntryId(e.target.value);
              if (e.target.value) setPassword('');
            }}
          >
            <option value="">Enter manually…</option>
            {credentialEntries.map((e) => (
              <option key={e.id} value={e.id}>
                Saved: {e.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {!vaultEntryId && (
        <div className="row" style={{ gap: 8 }}>
          <div className="field grow">
            <label className="field-label">SSH username</label>
            <input
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              placeholder="Blank uses the resource's saved SSH login"
            />
          </div>
          <div className="field grow">
            <label className="field-label">SSH password</label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
      )}

      <div className="col" style={{ gap: 6, margin: '4px 0 14px' }}>
        <label className="row" style={{ gap: 7, fontSize: 12.5, alignItems: 'center' }}>
          <input type="checkbox" checked={openFirewall} onChange={(e) => setOpenFirewall(e.target.checked)} />
          Open TCP {amiPort || '5038'} in the host firewall (firewalld, else iptables)
        </label>
        <label className="row" style={{ gap: 7, fontSize: 12.5, alignItems: 'center' }}>
          <input type="checkbox" checked={saveCredentials} onChange={(e) => setSaveCredentials(e.target.checked)} />
          Save the login on this resource once it verifies
        </label>
      </div>

      <div className="row" style={{ gap: 8, marginTop: 4, marginBottom: 16 }}>
        <button type="button" className="btn grow" disabled={busy !== null} onClick={() => run(false)}>
          {busy === 'preview' ? 'Checking…' : 'Preview commands'}
        </button>
        <button type="button" className="btn btn-primary grow" disabled={busy !== null || !result} onClick={() => run(true)}>
          {busy === 'apply' ? 'Applying…' : 'Apply'}
        </button>
      </div>

      {result && (
        <div className="col" style={{ gap: 8 }}>
          <div className="subtle" style={{ fontSize: 12, lineHeight: 1.6 }}>
            {result.facts.version ?? 'Asterisk version unknown'}
            {result.facts.freepbx ? ' · FreePBX detected' : ''} · AMI at{' '}
            <span className="mono">
              {result.amiHost}:{result.amiPort}
            </span>{' '}
            · seen from{' '}
            <span className="mono">{result.sourceAddress}</span> · ACL{' '}
            <span className="mono">{result.permits.join(', ')}</span>
            {!result.applied && ' — preview only, nothing has run yet.'}
          </div>

          {result.generatedSecret && (
            <div className="card" style={{ padding: '9px 11px', fontSize: 12.5 }}>
              Generated secret: <span className="mono">{result.generatedSecret}</span>
              <div className="subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                Stored encrypted on the resource once applied. This is the only time it is shown.
              </div>
            </div>
          )}

          {result.notes.map((note, i) => (
            <div key={i} className="card subtle" style={{ padding: '9px 11px', fontSize: 12 }}>
              {note}
            </div>
          ))}

          {result.facts.existingGeneral && !result.applied && (
            <details>
              <summary style={{ fontSize: 12.5, cursor: 'pointer' }}>manager.conf [general] as it is now</summary>
              <div className="mono subtle" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', marginTop: 6 }}>
                {result.facts.existingGeneral}
              </div>
            </details>
          )}

          <ProvisionSteps steps={result.steps} />

          {result.verified !== null && (
            <div
              className="card"
              style={{
                padding: '9px 11px',
                fontSize: 12.5,
                borderLeft: `3px solid ${result.verified ? 'var(--itr-success)' : 'var(--itr-critical)'}`,
              }}
            >
              {result.verified ? 'AMI login verified' : 'AMI login failed'}
              <div className="mono subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                {result.verifyMessage}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AccessPortForm({ resource, onSaved }: { resource: Resource; onSaved: () => void }) {
  const toast = useToast();
  const [ports, setPorts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      ACCESS_SERVICES.map((service) => {
        const value = resource.config[ACCESS_SERVICE_SPECS[service].portKey];
        return [service, value === undefined || value === null ? '' : String(value)];
      }),
    ),
  );
  const [username, setUsername] = useState(String(resource.config.username ?? ''));
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const config: Record<string, unknown> = {};
      for (const service of ACCESS_SERVICES) {
        const raw = ports[service]?.trim() ?? '';
        // null rather than '' so a cleared field genuinely goes back to the
        // service default instead of leaving an empty string behind.
        config[ACCESS_SERVICE_SPECS[service].portKey] = raw === '' ? null : Number(raw);
      }
      config.username = username.trim() || null;

      await api.patch(`/api/resources/${resource.id}`, { config });
      onSaved();
      toast('Access settings saved', 'good');
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p className="subtle" style={{ fontSize: 12.5, marginBottom: 16, lineHeight: 1.6 }}>
        Leave a port blank to use its default. Set it to <span className="mono">0</span> to hide that service — useful
        where telnet is disabled and you would rather not be offered it. Setting a port also switches a service on for
        devices that would not normally show it, which is how you get Winbox on a MikroTik we have not recognised yet.
      </p>

      {ACCESS_SERVICES.map((service) => {
        const spec = ACCESS_SERVICE_SPECS[service];
        return (
          <div className="field" key={service}>
            <label className="field-label">{spec.label} port</label>
            <input
              className="input"
              type="number"
              min={0}
              max={65535}
              placeholder={String(spec.defaultPort)}
              value={ports[service] ?? ''}
              onChange={(e) => setPorts((current) => ({ ...current, [service]: e.target.value }))}
            />
            <span className="field-help">{spec.help}</span>
          </div>
        );
      })}

      <div className="field">
        <label className="field-label">Username</label>
        <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" />
        <span className="field-help">Used in the SSH and Winbox links. Never used to log in on your behalf.</span>
      </div>

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save access settings'}
      </button>
    </form>
  );
}

/* ================================================================== */
/* Diagnostics                                                        */
/* ================================================================== */

const RESOURCE_TOOLS: Array<{ id: DiagnosticTool; label: string; icon: typeof Activity; permission: 'tools:run' | 'tools:scan' }> = [
  { id: 'ping', label: 'Ping', icon: Activity, permission: 'tools:run' },
  { id: 'traceroute', label: 'Traceroute', icon: Route, permission: 'tools:run' },
  { id: 'port-scan', label: 'Port scan', icon: Radar, permission: 'tools:scan' },
  { id: 'dns', label: 'DNS', icon: Search, permission: 'tools:run' },
  { id: 'http', label: 'HTTP', icon: Globe, permission: 'tools:run' },
];

export function ResourceDiagnosticsCard({ resource }: { resource: Resource }) {
  const { can } = useAuth();
  const available = RESOURCE_TOOLS.filter((t) => can(t.permission));
  const [tool, setTool] = useState<DiagnosticTool>(available[0]?.id ?? 'ping');
  const [options, setOptions] = useState<Record<string, unknown>>({});
  const run = useDiagnosticRun();
  const { reset } = run;

  // Every address the resource records — primary first, then secondaries. A
  // device answering on its LAN address but not its tunnel one needs testing
  // on each; the server accepts only these, never a typed address.
  const hosts = registeredHosts(resource);
  const [address, setAddress] = useState<string>(hosts[0] ?? '');
  const selected = hosts.includes(address) ? address : (hosts[0] ?? '');
  // HTTP probes the resource's configured web URL, not a bare address.
  const pickAddress = hosts.length > 1 && tool !== 'http';

  usePingTransitionAlerts(run, tool === 'ping' && Boolean(options.continuous), pickAddress ? `${resource.name} (${selected})` : resource.name);

  // The scan that says the most about this device: RouterOS services for a
  // MikroTik, management ports for other network gear.
  const portPreset = accessPortPreset(resource);

  useEffect(() => {
    reset();
    setOptions(tool === 'port-scan' ? { preset: portPreset } : {});
  }, [tool, portPreset, reset]);

  if (available.length === 0) return null;

  const start = () =>
    void run.start(
      `/api/tools/resource/${resource.id}/${tool}`,
      pickAddress ? { ...options, address: selected } : options,
      STREAMING_TOOLS.includes(tool),
    );

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <Terminal size={14} />
          <span className="card-title">Diagnostics</span>
          <span className="card-subtitle">{pickAddress ? selected : (accessHostFor(resource) ?? '')}</span>
        </div>
        <div className="card-body">
          <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
            {available.map((t) => (
              <button key={t.id} className={`btn btn-sm${tool === t.id ? ' btn-primary' : ''}`} onClick={() => setTool(t.id)}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {pickAddress && (
              <div>
                <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
                  Address
                </label>
                <select
                  className="select mono"
                  style={{ minWidth: 160 }}
                  value={selected}
                  disabled={run.running}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    reset();
                  }}
                >
                  {hosts.map((host, i) => (
                    <option key={host} value={host}>
                      {host}
                      {i === 0 ? ' (primary)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <ToolOptions tool={tool} options={options} onChange={setOptions} />

            {tool === 'http' && (
              <div>
                <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
                  Scheme
                </label>
                <select
                  className="select"
                  style={{ width: 140 }}
                  value={String(options.scheme ?? 'auto')}
                  onChange={(e) => setOptions((current) => ({ ...current, scheme: e.target.value }))}
                >
                  <option value="auto">Configured URL</option>
                  <option value="http">HTTP</option>
                  <option value="https">HTTPS</option>
                </select>
              </div>
            )}

            <span className="grow" />

            {run.running ? (
              <button className="btn btn-danger" onClick={run.stop}>
                <Square size={14} /> Stop
              </button>
            ) : (
              <button className="btn btn-primary" onClick={start}>
                <Play size={14} /> Run
              </button>
            )}
          </div>

          <DiagnosticProgress run={run} />
        </div>
      </div>

      <DiagnosticResults tool={tool} run={run} />
    </>
  );
}
